import { BusinessObjectBase } from './base';
import { activityItemSchema } from '@schemas/business-objects/activity-item';
import type { Activity } from './activity';
import type { ResourceSet } from './resource-set';
import type { SessionActivityItem } from './session-activity-item';
import type { ActivityType, Id } from '../base-types';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ActivityItemData {
  readonly id: Id;
  readonly name?: string;
  readonly type?: ActivityType;
  readonly required?: boolean;
  readonly validValues?: string;
  readonly sequence?: number;
  readonly activityId: Id;
  readonly activity?: Activity;
  readonly resourceSetId: Id;
  readonly resourceSet?: ResourceSet;
  readonly sessionActivityItems?: SessionActivityItem[];
  readonly locked?: boolean;
}

export interface ActivityItem extends Readonly<ActivityItemData> {}
export class ActivityItem extends BusinessObjectBase {
  static readonly entityName = 'ActivityItem' as const;
  static readonly plural = 'activity-items';
  static schema = activityItemSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ActivityItem[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ActivityItem(data));
  }

  static async findOne(filter?: any): Promise<ActivityItem | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ActivityItem(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ActivityItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ActivityItem(data);
  }

  static async create(
    data: Omit<ActivityItemData, 'id'>,
  ): Promise<ActivityItem> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ActivityItem(result);
  }

  static async createMany(
    data: Omit<ActivityItemData, 'id'>[],
  ): Promise<ActivityItem[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ActivityItem(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ActivityItemData>,
  ): Promise<ActivityItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ActivityItem(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ActivityItem.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
